import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MultiSelectFilter } from '../../components/MultiSelectFilter'
import { DateRangeFilter, type DateRangeValue } from '../../components/DateRangeFilter'
import { FilterEraser } from '../../components/FilterEraser'
import { CHANNEL_LABELS, MODE_LABELS } from '../../components/mcp/analytics/badges'
import {
  DEFAULT_PERIOD,
  PERIOD_DAYS,
  PERIOD_KEYS,
  type AnalyticsFilterState,
  type PeriodKey,
} from './useAnalyticsFilters'
import type { McpAnalyticsUser } from '../../lib/types'

/* ─── The shared filter bar (MS-700 AC7–9) ───

   User · Channel · Invocation Mode · Period, in that order, applying to all three
   tabs. It sits below the tab strip and outside the scroll area, so it is not
   affected by scrolling and does not have to be re-found after a long table.

   All four controls are the existing shared filter components, which already
   coordinate through `lib/popoverBus` so only one popover is open at a time.

   Channel and Invocation Mode option labels come from `badges.tsx` — the same
   constants the table cells render — so the filter can never offer a wording the
   rows do not use. They are product vocabulary and are not translated; the field
   *labels* around them are. */

export interface FilterBarProps {
  filters: AnalyticsFilterState
  onChange: (patch: Partial<AnalyticsFilterState>) => void
  /** Options for the User picker — the people active in the current period. */
  users: McpAnalyticsUser[]
}

function toDateRangeValue(
  filters: AnalyticsFilterState,
  labelFor: (key: PeriodKey) => string,
): DateRangeValue {
  if (filters.period === 'custom') {
    return { start: filters.customStart, end: filters.customEnd, periodLabel: null }
  }
  return { start: null, end: null, periodLabel: labelFor(filters.period) }
}

export function FilterBar({ filters, onChange, users }: FilterBarProps) {
  const { t } = useTranslation()
  // Incrementing a key remounts the MultiSelectFilter, resetting its internal
  // explicitlyEmpty flag so the eraser restores the "all checked" default.
  const [userKey, setUserKey] = useState(0)
  const [channelKey, setChannelKey] = useState(0)
  const [modeKey, setModeKey] = useState(0)

  const labelFor = (key: PeriodKey) => t(`mcpAnalytics.detail.periods.${key}` as const)
  const periods = PERIOD_KEYS.map(key => ({ label: labelFor(key), days: PERIOD_DAYS[key] }))

  const allLabel = t('mcpAnalytics.detail.filters.filterAll')

  function handlePeriodChange(next: DateRangeValue) {
    // A named period is stored by key, so a session restored tomorrow still means
    // "the last 12 months" rather than the 12 months ending yesterday.
    const matched = PERIOD_KEYS.find(key => labelFor(key) === next.periodLabel)
    if (matched) {
      onChange({ period: matched, customStart: null, customEnd: null })
      return
    }
    onChange({ period: 'custom', customStart: next.start, customEnd: next.end })
  }

  return (
    <div className="filter-card" style={{ position: 'static', paddingTop: 0 }}>
      <div className="filter-fields">
        <div className="filter-field">
          <div className="filter-eraser-row">
            <label>{t('mcpAnalytics.detail.filters.user')}</label>
            <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => { onChange({ users: [] }); setUserKey(k => k + 1) }} />
          </div>
          <MultiSelectFilter
            key={userKey}
            options={users.filter(u => u.email).map(user => ({ value: user.email, label: user.name || user.email }))}
            selected={filters.users}
            onChange={values => onChange({ users: values })}
            placeholder={t('mcpAnalytics.detail.filters.userPlaceholder')}
            allLabel={allLabel}
            ariaLabel={t('mcpAnalytics.detail.filters.user')}
            searchPlaceholder={t('mcpAnalytics.detail.filters.userSearchPlaceholder')}
          />
        </div>

        <div className="filter-field">
          <div className="filter-eraser-row">
            <label>{t('mcpAnalytics.detail.filters.channel')}</label>
            <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => { onChange({ channels: [] }); setChannelKey(k => k + 1) }} />
          </div>
          <MultiSelectFilter
            key={channelKey}
            options={[
              { value: 'agent', label: CHANNEL_LABELS.agent },
              { value: 'external', label: CHANNEL_LABELS.external },
            ]}
            selected={filters.channels}
            onChange={values => onChange({ channels: values })}
            placeholder={t('mcpAnalytics.detail.filters.channelPlaceholder')}
            allLabel={allLabel}
            ariaLabel={t('mcpAnalytics.detail.filters.channel')}
          />
        </div>

        <div className="filter-field">
          <div className="filter-eraser-row">
            <label>{t('mcpAnalytics.detail.filters.invocationMode')}</label>
            <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => { onChange({ invocationModes: [] }); setModeKey(k => k + 1) }} />
          </div>
          <MultiSelectFilter
            key={modeKey}
            options={[
              { value: 'batch', label: MODE_LABELS.batch },
              { value: 'single', label: MODE_LABELS.single },
            ]}
            selected={filters.invocationModes}
            onChange={values => onChange({ invocationModes: values })}
            placeholder={t('mcpAnalytics.detail.filters.invocationModePlaceholder')}
            allLabel={allLabel}
            ariaLabel={t('mcpAnalytics.detail.filters.invocationMode')}
          />
        </div>

        <div className="filter-field">
          <div className="filter-eraser-row">
            <label>{t('mcpAnalytics.detail.filters.period')}</label>
            <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => onChange({ period: DEFAULT_PERIOD, customStart: null, customEnd: null })} />
          </div>
          <DateRangeFilter
            periods={periods}
            value={toDateRangeValue(filters, labelFor)}
            onChange={handlePeriodChange}
            ariaLabel={t('mcpAnalytics.detail.filters.periodAria')}
          />
        </div>
      </div>
    </div>
  )
}
